import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Upload as UploadIcon, ArrowLeft, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const Upload = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error('Only PDF and image files are allowed');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/statements/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Statement uploaded successfully!');
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploading(false);
    }
  };

  const getFileIcon = () => {
    if (!file) return <FileText className="w-16 h-16 text-ledger-green" />;
    if (file.type === 'application/pdf') return <FileText className="w-16 h-16 text-ledger-orange" />;
    return <ImageIcon className="w-16 h-16 text-ledger-orange" />;
  };

  return (
    <div className="min-h-screen bg-ledger-bone relative noise-overlay">
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-black/10 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="font-mono text-xs text-gray-600 hover:text-ledger-green flex items-center gap-2"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
            </div>
            <span className="font-mono text-xs text-gray-600" data-testid="user-email">{user?.email}</span>
          </div>
        </header>

        {/* Upload Area */}
        <main className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl font-bold text-ledger-green mb-2" data-testid="upload-title">Upload Statement</h1>
          <p className="font-mono text-sm text-gray-600 mb-2">Upload your bank statement PDF or image file</p>
          {user?.subscription_status !== 'active' && user?.free_uploads_remaining > 0 && (
            <p className="font-mono text-xs text-ledger-orange mb-8">
              🎁 Free extraction - {user.free_uploads_remaining} remaining
            </p>
          )}
          {user?.subscription_status === 'active' && (
            <p className="font-mono text-xs text-green-600 mb-8">
              ✓ Unlimited uploads with your subscription
            </p>
          )}

          {/* The Scanner Bed */}
          <div
            className={`border-2 ${
              dragActive ? 'border-ledger-orange bg-ledger-orange/5' : 'border-ledger-green'
            } p-12 bg-white/50 backdrop-blur-sm text-center scanner-beam cursor-pointer transition-colors`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,image/jpeg,image/png,image/jpg"
              onChange={handleFileInputChange}
              data-testid="file-input"
            />

            <div className="mb-6">{getFileIcon()}</div>

            {file ? (
              <div className="space-y-4">
                <p className="font-mono text-sm text-ledger-green" data-testid="selected-filename">{file.name}</p>
                <p className="font-mono text-xs text-gray-600">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="font-mono text-xs text-gray-600 hover:text-ledger-green underline"
                  data-testid="change-file-btn"
                >
                  Change file
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="font-mono text-sm text-ledger-green">Drop your file here or click to browse</p>
                <p className="font-mono text-xs text-gray-600">PDF, JPG, or PNG • Max 10MB</p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          {file && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="bg-ledger-orange text-white rounded-none hover:bg-ledger-orange/90 uppercase tracking-widest text-xs py-4 px-8 font-mono disabled:opacity-50"
                data-testid="process-statement-btn"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-pulse">Processing...</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UploadIcon className="w-4 h-4" />
                    Process Statement
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Info */}
          <div className="mt-12 border-2 border-black/10 p-6 bg-white">
            <h3 className="font-mono text-xs uppercase tracking-widest text-ledger-green mb-4">What happens next?</h3>
            <ul className="space-y-2 font-mono text-xs text-gray-600">
              <li>• Your statement is processed by AI (Gemini 2.0 Flash)</li>
              <li>• All transactions are extracted automatically</li>
              <li>• You can review the data and download as CSV</li>
              <li>• Your data is securely stored in your account</li>
            </ul>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Upload;